// askSmart-ui/src/components/SMRTDocsSigningView.tsx
//
// Field-by-field signing experience. Replaces the immediate-pop confirm
// modal flow. The signer now:
//   1. Sees the document with placed fields overlaid.
//   2. Clicks each of their fields to fill in a signature / initial /
//      title / text value. Date fields auto-fill on open.
//   3. Submit is blocked until every required field for this signer is
//      filled.
//   4. Clicking Submit raises a final confirm-signing acknowledgement
//      overlay; only after that confirmation is the parent's onSubmit
//      called and the signing recorded.

import React, { useEffect, useRef, useState } from "react";
import { SMRTDocsPlacedField } from "./SMRTDocsFieldPlacementModal";
import { loadPdfjs } from "../utils/pdfWorker";

export interface SMRTDocsSigningResult {
  signature: string;
  initials: string;
  signatureType: "drawn" | "typed" | "uploaded";
  // Per-placement values keyed by placement.id. Lets the parent build a
  // signed PDF embedding the actual image the signer stamped into each
  // specific field rather than a single representative signature.
  fieldValues: Record<string, string>;
}

interface Props {
  isOpen: boolean;
  documentBlobName: string;
  documentName: string;
  ownerUserId: string;
  placements: SMRTDocsPlacedField[];
  mySignerIndex: number;
  // The current signer's display name, used to pre-populate the Adopt
  // Your Signature form. Editable inside the modal so a signer can add
  // a middle initial etc.
  mySignerName?: string;
  onClose: () => void;
  /**
   * May be async — the Submit button then runs a spinner until it settles.
   *
   * Signing an 80-page lease fetches the original, stamps it and uploads the
   * result, which takes long enough that a still button reads as one that
   * didn't register. Worse, the document behind the overlay still shows the
   * signature block empty until the parent closes the view, so the signer's
   * evidence agrees with them that nothing happened.
   */
  onSubmit: (result: SMRTDocsSigningResult) => void | Promise<void>;
}

// Handwriting-style font choices for the Select Type preview. Each entry
// stacks a system-installed handwriting font with a generic cursive
// fallback so the preview still looks scripted on machines that don't
// ship the named font.
const STYLE_OPTIONS: { label: string; font: string }[] = [
  {
    label: "Dancing Script",
    font: "'Dancing Script','Segoe Script',cursive",
  },
  {
    label: "Brush Script",
    font: "'Brush Script MT','Segoe Script',cursive",
  },
  {
    label: "Lucida Hand",
    font: "'Lucida Handwriting','Lucida Calligraphy',cursive",
  },
  {
    label: "Bradley Hand",
    font: "'Bradley Hand','Bradley Hand ITC',cursive",
  },
  {
    label: "Snell Roundhand",
    font: "'Snell Roundhand','Apple Chancery',cursive",
  },
  {
    label: "Monotype Corsiva",
    font: "'Monotype Corsiva','Comic Sans MS',cursive",
  },
];

// Generates a 16-char placeholder verification code displayed under the
// adopted signature card. The real per-signer SHA256(documentHash + …)
// code lands in a follow-up stage; until then this gives the card a
// plausible-looking identifier.
function generateVerificationCode(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let s = "";
  for (let i = 0; i < 16; i++) {
    s += chars[Math.floor(Math.random() * chars.length)];
  }
  return s;
}

// Default initials = the first letter of each whitespace-separated word
// in the signer's name. The signer can edit before adopting.
function defaultInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("")
    .slice(0, 6);
}

// Blue ink used for typed/drawn signatures and initials. Kept here so
// the canvas renderers, draw-mode stroke, and Adopt preview all match.
const INK_COLOR = "#1a4dad";

// Renders just the handwriting (no baked-in labels) so the PDF embed
// and adopt-modal preview can compose "Signed By:" / verification code
// around it consistently.
function renderSignatureCard(name: string, fontStack: string): string {
  const c = document.createElement("canvas");
  c.width = 600;
  c.height = 140;
  const ctx = c.getContext("2d");
  if (!ctx) return "";
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.fillStyle = INK_COLOR;
  ctx.font = `italic 80px ${fontStack}`;
  ctx.textBaseline = "middle";
  ctx.fillText(name, 12, c.height / 2);
  return c.toDataURL("image/png");
}

// Same approach for initials — blue handwriting only, narrower canvas
// reflecting the smaller field footprint.
function renderInitialsCard(initials: string, fontStack: string): string {
  const c = document.createElement("canvas");
  c.width = 260;
  c.height = 140;
  const ctx = c.getContext("2d");
  if (!ctx) return "";
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.fillStyle = INK_COLOR;
  ctx.font = `italic 80px ${fontStack}`;
  ctx.textBaseline = "middle";
  ctx.fillText(initials, 12, c.height / 2);
  return c.toDataURL("image/png");
}

export default function SMRTDocsSigningView({
  isOpen,
  documentBlobName,
  documentName,
  ownerUserId,
  placements,
  mySignerIndex,
  mySignerName = "",
  onClose,
  onSubmit,
}: Props) {
  // Rendered page bitmaps + native sizes so the signer scrolls through
  // the doc the same way the admin saw it when placing fields. Field
  // markers are positioned per-page (matches the placement modal so the
  // signer sees fields at the admin-defined spots).
  const [pages, setPages] = useState<
    { width: number; height: number; imageUrl: string }[]
  >([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldTypes, setFieldTypes] = useState<
    Record<string, "drawn" | "typed" | "uploaded">
  >({});
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  // "type" is the new "Select Type" preview mode; kept as the existing
  // key so the existing canvas / upload branches stay untouched.
  const [sigMode, setSigMode] = useState<"draw" | "type" | "upload">("type");
  const [uploadedSig, setUploadedSig] = useState("");
  const [textInput, setTextInput] = useState("");
  const [showAcknowledge, setShowAcknowledge] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);
  /** Set while the parent's onSubmit is in flight. */
  const [submitting, setSubmitting] = useState(false);
  // Adopt-Your-Signature inputs. Auto-populated from mySignerName, but
  // editable so signers can add middle initials etc.
  const [fullName, setFullName] = useState("");
  const [initialsValue, setInitialsValue] = useState("");
  const [styleIndex, setStyleIndex] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [adoptError, setAdoptError] = useState<string | null>(null);
  // Adopted signature/initials captured the first time the signer
  // clicks a signature or initial spot. Subsequent clicks apply the
  // stored values directly without re-opening the Adopt modal so each
  // spot is still an explicit per-click action by the signer.
  const [adoptedSignature, setAdoptedSignature] = useState<{
    signature: string;
    initials: string;
    signatureType: "drawn" | "typed" | "uploaded";
  } | null>(null);
  // Briefly outlined when the Next button jumps to an unfilled field.
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(
    null,
  );
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const drawnRef = useRef(false);

  // On open, seed date fields with today and clear the rest. Resets every
  // time the modal reopens so signers always start clean — matches the
  // "no persistence between sessions" requirement carried over from the
  // earlier acknowledge → sign modal.
  useEffect(() => {
    if (!isOpen) return;
    const today = new Date().toISOString().slice(0, 10);
    const seed: Record<string, string> = {};
    for (const p of placements) {
      if (p.signerIndex === mySignerIndex && p.type === "date") {
        seed[p.id] = today;
      }
    }
    setFieldValues(seed);
    setFieldTypes({});
    setEditingFieldId(null);
    setShowAcknowledge(false);
    setAcknowledged(false);
    setAckError(null);
    setAdoptedSignature(null);
    setHighlightedFieldId(null);
  }, [isOpen, placements, mySignerIndex]);

  // Render the PDF once per open via pdf.js so the signing canvas
  // matches the admin's placement modal exactly (same scale, same page
  // stack). Without this the signer sees fields at iframe-viewport
  // coords while admin placed at page-coord, so positions misalign.
  useEffect(() => {
    if (!isOpen) {
      setPages([]);
      return;
    }
    if (!documentBlobName) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(
            ownerUserId,
          )}/_?blobName=${encodeURIComponent(documentBlobName)}&format=base64`,
          { credentials: "include" },
        );
        if (!res.ok) throw new Error("Failed to fetch document");
        const { base64 } = await res.json();
        const bin = atob(base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const pdfjs = await loadPdfjs();
        const doc = await pdfjs.getDocument({ data: bytes }).promise;
        const TARGET_WIDTH = 800;
        const rendered: {
          width: number;
          height: number;
          imageUrl: string;
        }[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          if (cancelled) return;
          const page = await doc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = TARGET_WIDTH / base.width;
          const vp = page.getViewport({ scale });
          const c = document.createElement("canvas");
          c.width = vp.width;
          c.height = vp.height;
          const ctx = c.getContext("2d");
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
          rendered.push({
            width: vp.width,
            height: vp.height,
            imageUrl: c.toDataURL("image/png"),
          });
        }
        if (cancelled) return;
        setPages(rendered);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to render PDF for signing:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, documentBlobName, ownerUserId]);

  // Wipe the draw canvas when the Adopt editor opens or the user
  // switches back into draw mode so old strokes don't bleed into a fresh
  // signature. Triggered by signature OR initial fields since both route
  // through the same Adopt modal before any signature has been adopted.
  useEffect(() => {
    if (!editingFieldId) return;
    const f = placements.find((p) => p.id === editingFieldId);
    if (!f || (f.type !== "signature" && f.type !== "initial")) return;
    if (sigMode !== "draw") return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    drawnRef.current = false;
  }, [editingFieldId, sigMode, placements]);

  // Seed the Adopt-Your-Signature form whenever the editor opens for a
  // signature or initial field. Resets every reopen so a re-adopt run
  // starts from the assigned signer's name.
  useEffect(() => {
    if (!editingFieldId) return;
    const f = placements.find((p) => p.id === editingFieldId);
    if (!f || (f.type !== "signature" && f.type !== "initial")) return;
    setFullName(mySignerName || "");
    setInitialsValue(defaultInitials(mySignerName || ""));
    setStyleIndex(0);
    setVerificationCode(generateVerificationCode());
    setUploadedSig("");
    setAdoptError(null);
    setSigMode("type");
  }, [editingFieldId, placements, mySignerName]);

  if (!isOpen) return null;

  const myFields = placements.filter((p) => p.signerIndex === mySignerIndex);
  // Dates auto-fill, so they don't count toward the submit gate.
  const requiredFields = myFields.filter((p) => p.type !== "date");
  const allRequiredFilled =
    requiredFields.length === 0
      ? true
      : requiredFields.every(
          (f) =>
            !!fieldValues[f.id] && String(fieldValues[f.id]).trim() !== "",
        );
  const filledCount = requiredFields.filter(
    (f) => !!fieldValues[f.id] && String(fieldValues[f.id]).trim() !== "",
  ).length;
  const editingField = editingFieldId
    ? placements.find((p) => p.id === editingFieldId) || null
    : null;

  const openEditor = (f: SMRTDocsPlacedField) => {
    // Only the current signer can fill their fields. Date is auto-managed.
    if (f.signerIndex !== mySignerIndex) return;
    if (f.type === "date") return;

    // Signature / initial: if the signer has already adopted, just stamp
    // this specific spot with the adopted value — no modal re-open.
    // Otherwise the autofill effect seeds and the Adopt modal renders.
    if (
      (f.type === "signature" || f.type === "initial") &&
      adoptedSignature
    ) {
      const stamp =
        f.type === "signature"
          ? adoptedSignature.signature
          : adoptedSignature.initials;
      const nextValues = { ...fieldValues, [f.id]: stamp };
      setFieldValues(nextValues);
      if (f.type === "signature") {
        setFieldTypes((prev) => ({
          ...prev,
          [f.id]: adoptedSignature.signatureType,
        }));
      }
      goToNextRequiredAfter(nextValues);
      return;
    }

    setEditingFieldId(f.id);
    if (f.type !== "signature" && f.type !== "initial") {
      setTextInput(fieldValues[f.id] || "");
    }
  };

  const pointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * c.width,
      y: ((e.clientY - rect.top) / rect.height) * c.height,
    };
  };

  const onCanvasDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current;
    if (!c) return;
    drawingRef.current = true;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    (e.target as Element).setPointerCapture(e.pointerId);
  };

  const onCanvasMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = INK_COLOR;
    ctx.lineTo(x, y);
    ctx.stroke();
    drawnRef.current = true;
  };

  const onCanvasUp = () => {
    drawingRef.current = false;
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    drawnRef.current = false;
  };

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setUploadedSig(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const saveSignatureEditor = () => {
    if (!editingField) return;
    if (!fullName.trim()) {
      setAdoptError("Full name is required");
      return;
    }
    if (!initialsValue.trim()) {
      setAdoptError("Initials are required");
      return;
    }
    let value = "";
    let type: "drawn" | "typed" | "uploaded" = "typed";
    if (sigMode === "draw") {
      if (!drawnRef.current) {
        setAdoptError("Please draw your signature");
        return;
      }
      value = canvasRef.current?.toDataURL("image/png") || "";
      type = "drawn";
    } else if (sigMode === "type") {
      value = renderSignatureCard(
        fullName.trim(),
        STYLE_OPTIONS[styleIndex].font,
      );
      type = "typed";
    } else {
      if (!uploadedSig) {
        setAdoptError("Please upload a signature image");
        return;
      }
      value = uploadedSig;
      type = "uploaded";
    }
    setAdoptError(null);
    // Adopt once, stamp per-click: cache the adopted signature so later
    // clicks can stamp directly, but only fill the spot the signer
    // clicked to open this modal. They must click each remaining spot
    // explicitly. Initials are always rendered as a styled blue-ink image
    // so they match the signature presentation.
    const adopted = {
      signature: value,
      initials: renderInitialsCard(
        initialsValue.trim().toUpperCase(),
        STYLE_OPTIONS[styleIndex].font,
      ),
      signatureType: type,
    };
    setAdoptedSignature(adopted);
    const stamp =
      editingField.type === "signature"
        ? adopted.signature
        : adopted.initials;
    const nextValues = { ...fieldValues, [editingField.id]: stamp };
    setFieldValues(nextValues);
    if (editingField.type === "signature") {
      setFieldTypes((prev) => ({
        ...prev,
        [editingField.id]: adopted.signatureType,
      }));
    }
    setEditingFieldId(null);
    goToNextRequiredAfter(nextValues);
  };

  const saveTextEditor = () => {
    if (!editingField) return;
    if (!textInput.trim()) return;
    const nextValues = {
      ...fieldValues,
      [editingField.id]: textInput.trim(),
    };
    setFieldValues(nextValues);
    setEditingFieldId(null);
    goToNextRequiredAfter(nextValues);
  };

  const confirmSubmit = async () => {
    if (submitting) return;
    if (!acknowledged) {
      setAckError("You must confirm before submitting");
      return;
    }
    // Pick a representative signature + initials value to pass back to
    // the parent so the existing signing logic (which expects a single
    // result shape) stays unchanged.
    const sigField = myFields.find((f) => f.type === "signature");
    const sigVal = sigField ? fieldValues[sigField.id] || "" : "";
    const sigType: "drawn" | "typed" | "uploaded" =
      sigField && fieldTypes[sigField.id] ? fieldTypes[sigField.id] : "drawn";
    const initField = myFields.find((f) => f.type === "initial");
    const initVal = initField ? fieldValues[initField.id] || "" : "";
    setSubmitting(true);
    try {
      await onSubmit({
        signature: sigVal,
        initials: initVal,
        signatureType: sigType,
        fieldValues: { ...fieldValues },
      });
    } finally {
      // Cleared even on success. The parent normally closes this view once the
      // signature is stored, but if it stays open — a failure it handled, or a
      // flow that keeps the signer here — the button has to come back or they
      // are stuck looking at a spinner with nothing to press.
      setSubmitting(false);
    }
  };

  // Highlights a field (yellow glow already implemented) and scrolls
  // the document so the field is roughly centered in the wrapper.
  // Works across pages because field positions are computed off the
  // page stack accumulated height.
  const gotoField = (fieldId: string) => {
    setHighlightedFieldId(fieldId);
    window.setTimeout(() => setHighlightedFieldId(null), 2500);
    const field = placements.find((p) => p.id === fieldId);
    if (!field) return;
    const wrapper = wrapperRef.current;
    if (!wrapper || pages.length === 0) return;
    const pageIdx = typeof field.page === "number" ? field.page : 0;
    const page = pages[pageIdx];
    if (!page) return;
    const pageTop = pages
      .slice(0, pageIdx)
      .reduce((acc, x) => acc + x.height, 0);
    const fieldTop = pageTop + field.y * page.height;
    const targetTop = Math.max(0, fieldTop - wrapper.clientHeight / 2 + 18);
    wrapper.scrollTo({ top: targetTop, behavior: "smooth" });
  };

  // Finds the next unfilled required field given an optimistic field-
  // values map (so we don't have to wait for React state to flush) and
  // jumps to it. No-op when every required field is filled.
  const goToNextRequiredAfter = (filledMap: Record<string, string>) => {
    const requiredAfter = placements
      .filter((p) => p.signerIndex === mySignerIndex && p.type !== "date");
    const next = requiredAfter.find(
      (f) => !filledMap[f.id] || String(filledMap[f.id]).trim() === "",
    );
    if (next) gotoField(next.id);
  };

  // Signer-facing label for a field type. "text-encrypted" is presented as
  // plain "Text" — from the signer's perspective it behaves identically to a
  // text field; the encryption is a storage concern they don't need to see.
  const typeDisplayName = (t: string) =>
    t === "text-encrypted" ? "Text" : t;

  const fieldLabel = (f: SMRTDocsPlacedField) => {
    const v = fieldValues[f.id];
    if (f.type === "signature") {
      return v && v.startsWith("data:image") ? "✓ Signed" : "Signature";
    }
    if (f.type === "initial") {
      return v && v.startsWith("data:image") ? "✓ Initialed" : "Initial";
    }
    if (f.type === "date") return v || "";
    if (v && String(v).trim() !== "") return v;
    return typeDisplayName(f.type);
  };

  return (
    <div className="smrtdocs-modal-overlay" style={{ zIndex: 10000 }}>
      <div
        className="smrtdocs-modal"
        style={{
          width: "92vw",
          height: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            Sign — {documentName}
          </h2>
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>

        <div
          style={{
            flex: 1,
            position: "relative",
            background: "#1c1c1c",
            overflow: "hidden",
          }}
        >
          <div
            ref={wrapperRef}
            style={{
              position: "absolute",
              inset: 0,
              overflow: "auto",
            }}
          >
            {pages.length === 0 ? (
              <div style={{ padding: 20, color: "#9aa0a6" }}>
                Loading document…
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  width: pages[0].width,
                  margin: "0 auto",
                }}
              >
                {pages.map((pg, i) => (
                  <img
                    key={i}
                    src={pg.imageUrl}
                    alt={`${documentName} page ${i + 1}`}
                    width={pg.width}
                    height={pg.height}
                    draggable={false}
                    style={{
                      display: "block",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  />
                ))}

                {placements.map((p) => {
                  // Only render this signer's own fields. Other signers'
                  // placements are hidden entirely — no placeholder, no
                  // awaiting state — so the signer sees a clean document
                  // with just their own work.
                  if (p.signerIndex !== mySignerIndex) return null;
                  const pageIdx =
                    typeof p.page === "number" ? p.page : 0;
                  const page = pages[pageIdx];
                  if (!page) return null;
                  const pageTop = pages
                    .slice(0, pageIdx)
                    .reduce((acc, x) => acc + x.height, 0);
                  const isMine = p.signerIndex === mySignerIndex;
                  const value = fieldValues[p.id];
                  const filled =
                    !!value && String(value).trim() !== "";
                  const isImageValue =
                    (p.type === "signature" || p.type === "initial") &&
                    typeof value === "string" &&
                    value.startsWith("data:image");
                  const isSigOrInitial =
                    p.type === "signature" || p.type === "initial";
                  const isHighlighted = highlightedFieldId === p.id;
                  const fieldW = p.type === "initial" ? 70 : 140;
                  const fieldH = p.type === "initial" ? 36 : 36;
                  return (
                    <div
                      key={p.id}
                      onClick={() => openEditor(p)}
                      style={{
                        position: "absolute",
                        left: p.x * page.width,
                        top: pageTop + p.y * page.height,
                        width: fieldW,
                        height: fieldH,
                        background: filled
                          ? "rgba(220, 255, 220, 0.95)"
                          : "rgba(255, 255, 255, 0.92)",
                        border: isHighlighted
                          ? "3px solid #ffd700"
                          : `1px solid ${isMine ? "#4a90e299" : "#99999999"}`,
                        borderLeft:
                          isHighlighted
                            ? "3px solid #ffd700"
                            : isSigOrInitial
                              ? `4px solid ${isMine ? "#4a90e2" : "#999"}`
                              : `2px solid ${isMine ? "#4a90e2" : "#999"}`,
                        borderRadius: 4,
                        fontSize: "0.75rem",
                        color: "#111",
                        display: "flex",
                        alignItems: "center",
                        padding: isImageValue ? 0 : "0 6px",
                        cursor:
                          isMine && p.type !== "date"
                            ? "pointer"
                            : "default",
                        userSelect: "none",
                        boxShadow: isHighlighted
                          ? "0 0 16px rgba(255, 215, 0, 0.9)"
                          : "0 2px 4px rgba(0, 0, 0, 0.3)",
                        opacity: isMine ? 1 : 0.55,
                        overflow: "hidden",
                      }}
                      title={
                        isMine
                          ? p.type === "date"
                            ? "Date (auto-filled)"
                            : `${p.type} — click to fill`
                          : `Other signer's ${p.type}`
                      }
                    >
                      {isImageValue ? (
                        <img
                          src={value}
                          alt={p.type}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            pointerEvents: "none",
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            flex: 1,
                            textTransform: "capitalize",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fieldLabel(p)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Centered Next / Submit button — jumps the signer to the next
              unfilled required field, or opens the confirm-signing
              acknowledgement once everything is filled. */}
          <button
            onClick={() => {
              const next = requiredFields.find(
                (f) =>
                  !fieldValues[f.id] ||
                  String(fieldValues[f.id]).trim() === "",
              );
              if (next) {
                gotoField(next.id);
                return;
              }
              setAcknowledged(false);
              setAckError(null);
              setShowAcknowledge(true);
            }}
            style={{
              position: "absolute",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              padding: "10px 36px",
              background: allRequiredFilled ? "#1faa59" : "#4a90e2",
              color: "#fff",
              border: "none",
              borderRadius: 999,
              fontSize: "0.95rem",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
              zIndex: 5,
            }}
          >
            {allRequiredFilled ? "Submit" : "Next"}
          </button>
        </div>

        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid #2a2a2a",
            color: "#9aa0a6",
            fontSize: "0.85rem",
            textAlign: "right",
          }}
        >
          {filledCount} / {requiredFields.length} required fields completed
        </div>

        {editingField &&
          (editingField.type === "signature" ||
            editingField.type === "initial") && (
          <div
            className="smrtdocs-modal-overlay"
            style={{ zIndex: 10001 }}
            onClick={() => setEditingFieldId(null)}
          >
            <div
              className="smrtdocs-modal"
              style={{ width: 720 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>
                  Adopt Your Signature
                </h2>
                <button
                  className="btn-cancel"
                  onClick={() => setEditingFieldId(null)}
                >
                  Close
                </button>
              </div>
              <div className="smrtdocs-modal-body" style={{ padding: 16 }}>
                <p
                  style={{
                    marginTop: 0,
                    marginBottom: 14,
                    color: "#9aa0a6",
                    fontSize: "0.9rem",
                  }}
                >
                  Confirm your name, initials, and signature.
                </p>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    marginBottom: 14,
                  }}
                >
                  <div style={{ flex: 2 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.8rem",
                        color: "#9aa0a6",
                        marginBottom: 4,
                      }}
                    >
                      Full Name
                    </label>
                    <input
                      className="manage-input"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.8rem",
                        color: "#9aa0a6",
                        marginBottom: 4,
                      }}
                    >
                      Initials
                    </label>
                    <input
                      className="manage-input"
                      value={initialsValue}
                      onChange={(e) =>
                        setInitialsValue(e.target.value.toUpperCase())
                      }
                      maxLength={6}
                      style={{
                        width: "100%",
                        textTransform: "uppercase",
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    marginBottom: 10,
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    className={sigMode === "type" ? "btn-save" : "btn-cancel"}
                    onClick={() => setSigMode("type")}
                  >
                    Select Type
                  </button>
                  <button
                    className={sigMode === "draw" ? "btn-save" : "btn-cancel"}
                    onClick={() => setSigMode("draw")}
                  >
                    Draw
                  </button>
                  <button
                    className={
                      sigMode === "upload" ? "btn-save" : "btn-cancel"
                    }
                    onClick={() => setSigMode("upload")}
                  >
                    Upload
                  </button>
                  {sigMode === "type" && (
                    <div
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <label
                        style={{ fontSize: "0.8rem", color: "#9aa0a6" }}
                      >
                        Change Style
                      </label>
                      <select
                        className="manage-input"
                        value={styleIndex}
                        onChange={(e) =>
                          setStyleIndex(parseInt(e.target.value, 10) || 0)
                        }
                        style={{ width: 200 }}
                      >
                        {STYLE_OPTIONS.map((s, i) => (
                          <option key={i} value={i}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {sigMode === "type" && (
                  <div style={{ display: "flex", gap: 12 }}>
                    <div
                      style={{
                        flex: 2,
                        padding: 16,
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderLeft: `4px solid ${INK_COLOR}`,
                        borderRadius: 4,
                        color: "#111",
                        minHeight: 160,
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                          color: "#666",
                        }}
                      >
                        <span>Signed By:</span>
                        <span>
                          {new Date().toLocaleDateString("en-US")}
                        </span>
                      </div>
                      <div
                        style={{
                          fontFamily: STYLE_OPTIONS[styleIndex].font,
                          fontSize: "2.6rem",
                          fontStyle: "italic",
                          color: INK_COLOR,
                          margin: "6px 0 10px",
                          wordBreak: "break-word",
                          lineHeight: 1.1,
                        }}
                      >
                        {fullName || " "}
                      </div>
                      <div
                        style={{
                          fontFamily: "monospace",
                          fontSize: "0.78rem",
                          color: "#666",
                        }}
                      >
                        {verificationCode}
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        padding: 16,
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderLeft: `4px solid ${INK_COLOR}`,
                        borderRadius: 4,
                        color: "#111",
                        minHeight: 160,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: STYLE_OPTIONS[styleIndex].font,
                          fontSize: "2.6rem",
                          fontStyle: "italic",
                          color: INK_COLOR,
                          lineHeight: 1.1,
                          wordBreak: "break-word",
                        }}
                      >
                        {(initialsValue || " ").toUpperCase()}
                      </div>
                      <div
                        style={{
                          fontFamily: "monospace",
                          fontSize: "0.78rem",
                          color: "#666",
                        }}
                      >
                        {verificationCode}
                      </div>
                    </div>
                  </div>
                )}

                {sigMode === "draw" && (
                  <div>
                    <canvas
                      ref={canvasRef}
                      width={600}
                      height={160}
                      style={{
                        width: "100%",
                        maxWidth: 600,
                        height: 160,
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        background: "#fff",
                        touchAction: "none",
                      }}
                      onPointerDown={onCanvasDown}
                      onPointerMove={onCanvasMove}
                      onPointerUp={onCanvasUp}
                      onPointerLeave={onCanvasUp}
                    />
                    <button
                      className="btn-cancel"
                      style={{ marginTop: 6 }}
                      onClick={clearCanvas}
                    >
                      Clear
                    </button>
                  </div>
                )}

                {sigMode === "upload" && (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onUpload}
                    />
                    {uploadedSig && (
                      <img
                        src={uploadedSig}
                        alt="signature preview"
                        style={{
                          marginTop: 8,
                          maxWidth: 300,
                          maxHeight: 120,
                          border: "1px solid #ccc",
                        }}
                      />
                    )}
                  </div>
                )}

                {adoptError && (
                  <div
                    style={{
                      color: "#c0392b",
                      fontSize: "0.85rem",
                      marginTop: 12,
                    }}
                  >
                    {adoptError}
                  </div>
                )}

                <p
                  style={{
                    fontSize: "0.78rem",
                    color: "#9aa0a6",
                    marginTop: 16,
                    lineHeight: 1.45,
                  }}
                >
                  By selecting Adopt and Sign, I agree that the signature
                  and initials will be the electronic representation of my
                  signature and initials for all purposes when I (or my
                  agent) use them on documents, including legally binding
                  contracts.
                </p>

                <div
                  style={{
                    marginTop: 14,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}
                >
                  <button
                    className="btn-cancel"
                    onClick={() => setEditingFieldId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-save"
                    onClick={saveSignatureEditor}
                  >
                    Adopt &amp; Sign
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {editingField &&
          editingField.type !== "signature" &&
          editingField.type !== "initial" &&
          editingField.type !== "date" && (
            <div
              className="smrtdocs-modal-overlay"
              style={{ zIndex: 10001 }}
              onClick={() => setEditingFieldId(null)}
            >
              <div
                className="smrtdocs-modal"
                style={{ width: 400 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="smrtdocs-modal-header">
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "1rem",
                      textTransform: "capitalize",
                    }}
                  >
                    {typeDisplayName(editingField.type)}
                  </h2>
                  <button
                    className="btn-cancel"
                    onClick={() => setEditingFieldId(null)}
                  >
                    Close
                  </button>
                </div>
                <div className="smrtdocs-modal-body" style={{ padding: 16 }}>
                  <input
                    className="manage-input"
                    placeholder={`Enter ${typeDisplayName(editingField.type)}`}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    autoFocus
                  />
                  <div style={{ marginTop: 14, textAlign: "right" }}>
                    <button className="btn-save" onClick={saveTextEditor}>
                      Set
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        {showAcknowledge && (
          <div
            className="smrtdocs-modal-overlay"
            style={{ zIndex: 10001 }}
            onClick={() => setShowAcknowledge(false)}
          >
            <div
              className="smrtdocs-modal"
              style={{ width: 440 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>
                  Confirm and Submit Signature
                </h2>
                <button
                  className="btn-cancel"
                  onClick={() => setShowAcknowledge(false)}
                >
                  Close
                </button>
              </div>
              <div className="smrtdocs-modal-body" style={{ padding: 16 }}>
                {ackError && (
                  <div
                    style={{
                      color: "#c0392b",
                      marginBottom: 10,
                      fontSize: "0.9rem",
                    }}
                  >
                    {ackError}
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: "0.85rem", color: "#777" }}>
                    Document
                  </div>
                  <div style={{ fontWeight: 600 }}>{documentName}</div>
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: "0.9rem",
                      color: "#333",
                    }}
                  >
                    You are signing as: {mySignerName || ""}
                  </div>
                </div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    I confirm that I have completed this document and that
                    my signature represents my intent to be legally bound
                    by its contents.
                  </span>
                </label>
                <div
                  style={{
                    marginTop: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 12,
                  }}
                >
                  {/* Said in words as well as shown, because the document
                      behind this overlay still has an empty signature block
                      until the upload finishes — without this the signer's own
                      eyes tell them it failed. */}
                  {submitting && (
                    <span style={{ fontSize: "0.85rem", color: "#9aa0a6" }}>
                      Applying your signature — large documents can take a
                      moment. Don't close this window.
                    </span>
                  )}
                  <button
                    className={acknowledged ? "btn-save" : "btn-cancel"}
                    onClick={confirmSubmit}
                    disabled={!acknowledged || submitting}
                    style={{
                      cursor:
                        acknowledged && !submitting ? "pointer" : "not-allowed",
                      opacity: acknowledged && !submitting ? 1 : 0.6,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {submitting && <span className="spinner" />}
                    {submitting ? "Submitting…" : "Submit Signature"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
